import { useEffect, useRef, useState } from 'react';
import { CATEGORY_LABEL, WORKFORCE, type WorkforceCard } from '../lib/aiWorkforce';
import '../styles/watch-console.css';

/*
 * The landing page's "your properties should not need you to watch everything"
 * section used to be a heading and nothing else. This shows the watching.
 *
 * Every line on it comes from WORKFORCE, the same roster the AI workforce
 * section renders and tests/aiworkforce.test.ts pins to the server. So it
 * carries no invented account figure and cannot claim an agent the product
 * does not run: only live agents appear, and their words are their own.
 *
 * It steps through the agents on its own while it is on screen, stops for good
 * once a visitor picks one, and stays still under prefers-reduced-motion.
 */

const STEP_MS = 5200;

function useReducedMotion(): boolean {
  const [r, setR] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setR(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return r;
}

function useInView<T extends Element>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => setInView(!!e?.isIntersecting), { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView] as const;
}

const LIVE: WorkforceCard[] = WORKFORCE.filter((c) => c.live && c.code !== 'assistant');

export function WatchConsole() {
  const reduced = useReducedMotion();
  const [ref, inView] = useInView<HTMLDivElement>();
  const [i, setI] = useState(0);
  const [held, setHeld] = useState(false);
  const [taken, setTaken] = useState(false);
  const playing = inView && !held && !taken && !reduced;
  const cur = LIVE[i]!;

  useEffect(() => {
    if (!playing) return;
    const t = window.setTimeout(() => setI((n) => (n + 1) % LIVE.length), STEP_MS);
    return () => window.clearTimeout(t);
  }, [playing, i]);

  const pick = (n: number) => { setTaken(true); setI(n); };

  return (
    <div className="wc" ref={ref}
      onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)} onBlur={() => setHeld(false)}>
      <ol className="wc-loop" aria-label="How the watching works">
        <li><b>Watch</b><span>Every record, every night</span></li>
        <li><b>Flag</b><span>What needs you, and why</span></li>
        <li><b>You decide</b><span>The agents ask before they act</span></li>
      </ol>

      <div className={`wc-screen wc-${cur.category}`}>
        <div className="wc-bar" aria-hidden>
          <span className="wc-pulse" />
          <span>{playing ? 'Watching now' : 'Watching'}</span>
          <span className="wc-count">{i + 1} of {LIVE.length}</span>
        </div>
        <div className="wc-body" key={cur.code} role="tabpanel" aria-live="polite">
          <div className="wc-who">
            <span className="wc-glyph" aria-hidden>{cur.glyph}</span>
            <span>
              <b>{cur.name}</b>
              <small>{CATEGORY_LABEL[cur.category]} · {cur.short}</small>
            </span>
          </div>
          <dl>
            <div><dt>It watches</dt><dd>{cur.monitors}</dd></div>
            <div><dt>So it can tell you</dt><dd>{cur.understands}</dd></div>
          </dl>
          {cur.actions[0] && <p className="wc-ask"><span>Ask it</span>“{cur.actions[0]}”</p>}
        </div>
        {playing && <span className="wc-timer" key={`t-${i}`} style={{ animationDuration: `${STEP_MS}ms` }} aria-hidden />}
      </div>

      <div className="wc-dots" role="tablist" aria-label="Pick an agent">
        {LIVE.map((a, n) => (
          <button key={a.code} type="button" role="tab" aria-selected={n === i} aria-label={a.name}
            title={a.name} className={`wc-dot wc-${a.category} ${n === i ? 'on' : ''}`} onClick={() => pick(n)}>
            <span aria-hidden>{a.glyph}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** One quiet line under the hero: which agent is watching what, in turn. */
export function HeroTicker() {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const t = window.setInterval(() => setI((n) => (n + 1) % LIVE.length), 3600);
    return () => window.clearInterval(t);
  }, [reduced]);
  const cur = LIVE[i]!;
  return (
    <a className="lp-ticker" href="#problem" aria-label={`${LIVE.length} AI agents are watching your portfolio. See what they watch.`}>
      <span className="wc-pulse" aria-hidden />
      <span className="lp-ticker-txt" key={cur.code} aria-hidden>
        <b>{cur.name}</b> {cur.short.charAt(0).toLowerCase() + cur.short.slice(1)}
      </span>
    </a>
  );
}
